// src/utils/api.js

const API_URL = 'https://terrasse-asie.resto-go.com/api-dyn.php';

/**
 * Envoie une requête POST à l'API
 * @param {Object} payload - Données au format attendu par l'API (table, action, data, etc.)
 * @returns {Promise<Object>} - Réponse JSON
 */
export async function postToApi(payload) {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.error || 'Erreur API');
    }

    return data;
  } catch (error) {
    console.error('Erreur lors de la requête API :', error);
    throw error;
  }
}

/**
 * Insère une commande dans la base
 * @param {Object} commande - Données de la commande (clientInfo, cartItems, total)
 * @returns {Promise<Object>} - Réponse API
 */
export function insererCommande(commande) {
  const payload = {
    table: 'commandes',
    action: 'insert',
    data: {
      ...commande,
      created_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
    }
  };

  return postToApi(payload);
}

// src/utils/api.js
export async function loginUser(email, password) {
  const payload = { table: 'users', action: 'get', where: `email='${email}'` };
  const res = await postToApi(payload);
  if (!res.success || res.count === 0) {
    throw new Error('Utilisateur non trouvé');
  }
  const user = res.data[0];
  // Comparer hash côté client seulement si hash simple (non sécurisé)
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(password));
  const hashHex = Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
  if (hashHex !== user.password_hash) {
    throw new Error('Mot de passe incorrect');
  }
  return user;
}

export async function getCommandes() {
  const payload = { table: 'commandes', action: 'get', order_by: 'created_at DESC' };
  return postToApi(payload);
}

export async function updateCommandeStatus(id, updates) {
  const payload = {
    table: 'commandes',
    action: 'update',
    where: `id=${id}`,
    data: updates // Permet de mettre à jour plusieurs champs
  };

 // console.log('Payload envoyé à l\'API pour updateCommandeStatus:', payload); // Ajout d'un log pour débogage

  return postToApi(payload);
}

const CARTE_API_URL = 'https://terrasse-asie.resto-go.com/api-carte.php';

async function cartePost(body) {
  let response;
  try {
    response = await fetch(CARTE_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  } catch (e) {
    throw new Error('Impossible de joindre le serveur (réseau)');
  }
  let data;
  try {
    data = await response.json();
  } catch (e) {
    throw new Error(`Réponse invalide du serveur (HTTP ${response.status}) — api-carte.php est-il déployé ?`);
  }
  if (!data.success) throw new Error(data.error || 'Erreur API carte');
  return data;
}

export async function saveCarteJson(carte, user) {
  return cartePost({ action: 'save', email: user.email, passwordHash: user.password_hash, data: carte });
}

export async function listBackups() {
  const response = await fetch(CARTE_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'list-backups' })
  });
  const data = await response.json();
  if (!data.success) throw new Error(data.error || 'Erreur liste backups');
  return data.backups;
}

export async function restoreBackup(file, user) {
  return cartePost({ action: 'restore', file, email: user.email, passwordHash: user.password_hash });
}

export async function deleteBackup(file, user) {
  return cartePost({ action: 'delete-backup', file, email: user.email, passwordHash: user.password_hash });
}

/**
 * Récupère la ligne unique de réglages (settings), ou null si absente/erreur
 */
export async function getSettings() {
  try {
    const res = await postToApi({ table: 'settings', action: 'get', order_by: 'id ASC', limit: 1 });
    if (res.success && Array.isArray(res.data) && res.data.length > 0) {
      return res.data[0];
    }
    return null;
  } catch (e) {
    console.error('Erreur lors du chargement des réglages :', e);
    return null;
  }
}

/**
 * Crée ou met à jour la ligne unique de réglages (settings)
 */
export async function saveSettings(data) {
  const existing = await getSettings();
  if (existing) {
    return postToApi({ table: 'settings', action: 'update', where: `id=${existing.id}`, data });
  }
  return postToApi({ table: 'settings', action: 'insert', data });
}

